import React, { useState, useRef, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface PortfolioImage {
  id: string;
  category: string;
  image_url: string;
  storage_path: string;
  created_at: string;
}

interface ProjectCategory {
  id: string;
  title: string;
  icon: string;
  description: string;
  images: PortfolioImage[];
}

const DEFAULT_CATEGORIES = [
  {
    id: 'interior',
    title: 'Interior Painting',
    icon: 'ri-home-4-line',
    description: 'Beautiful interior transformations',
  },
  {
    id: 'exterior',
    title: 'Exterior Painting',
    icon: 'ri-building-2-line',
    description: 'Stunning exterior makeovers',
  },
  {
    id: 'spray',
    title: 'Spray Jobs',
    icon: 'ri-spray-line',
    description: 'Factory-grade spray finishes',
  },
  {
    id: 'flooring',
    title: 'Flooring',
    icon: 'ri-layout-grid-line',
    description: 'Premium flooring installations',
  }
];

export const PortfolioSection: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState('interior');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);
  const [categories, setCategories] = useState<ProjectCategory[]>(
    DEFAULT_CATEGORIES.map(cat => ({ ...cat, images: [] }))
  );
  const [loading, setLoading] = useState(true);
  const sliderRef = useRef<HTMLDivElement>(null);

  // Load images from Supabase on mount
  useEffect(() => {
    loadImages();
  }, []);

  const loadImages = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('portfolio_images')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (data) {
        setCategories(prev => prev.map(cat => ({
          ...cat,
          images: data.filter((img: PortfolioImage) => img.category === cat.id)
        })));
      }
    } catch (error) {
      console.error('Error loading images:', error);
    } finally {
      setLoading(false);
    }
  };

  const activeData = categories.find(cat => cat.id === activeCategory) || categories[0];

  useEffect(() => {
    setCurrentIndex(0);
  }, [activeCategory]);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? activeData.images.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === activeData.images.length - 1 ? 0 : prev + 1));
  };

  const handleImageClick = () => {
    setShowLightbox(true);
  };

  const handleLightboxPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? activeData.images.length - 1 : prev - 1));
  };

  const handleLightboxNext = () => {
    setCurrentIndex((prev) => (prev === activeData.images.length - 1 ? 0 : prev + 1));
  };

  if (loading) {
    return (
      <section id="portfolio" className="py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <div className="inline-block w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
            <p className="mt-4 text-gray-600">Loading portfolio...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="portfolio" className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-teal-100 rounded-full mb-6">
            <i className="ri-gallery-line text-teal-600 mr-2"></i>
            <span className="text-sm font-semibold text-teal-700">Our Portfolio</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Completed Projects Gallery
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Explore our extensive portfolio of successful projects across Toronto and the GTA
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`group flex items-center px-6 py-3 rounded-full font-semibold transition-all duration-300 whitespace-nowrap ${
                activeCategory === category.id
                  ? 'bg-teal-600 text-white shadow-lg scale-105'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              <i className={`${category.icon} text-xl mr-2`}></i>
              {category.title}
              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-bold ${
                activeCategory === category.id
                  ? 'bg-white/20 text-white'
                  : 'bg-gray-200 text-gray-600'
              }`}>
                {category.images.length}
              </span>
            </button>
          ))}
        </div>

        {/* Category Description */}
        <div className="mb-8">
          <p className="text-lg text-gray-600 text-center">{activeData.description}</p>
        </div>

        {/* Image Slider or Empty State */}
        {activeData.images.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border-2 border-dashed border-gray-300">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-image-line text-4xl text-gray-400"></i>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No images yet</h3>
            <p className="text-gray-600">Check back soon for our latest projects</p>
          </div>
        ) : (
          <div className="relative">
            {/* Main Slider Container */}
            <div className="relative overflow-hidden rounded-2xl" ref={sliderRef}>
              <div className="flex items-center justify-center py-8">
                {/* Previous Image (Blurred) */}
                {activeData.images.length > 1 && (
                  <div className="hidden md:block w-1/4 px-2">
                    <div className="relative h-64 rounded-xl overflow-hidden opacity-40 blur-sm">
                      <img
                        src={activeData.images[(currentIndex - 1 + activeData.images.length) % activeData.images.length].image_url}
                        alt="Previous"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                )}

                {/* Current Image (Main) */}
                <div className="w-full md:w-1/2 px-2">
                  <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl group">
                    <img
                      src={activeData.images[currentIndex].image_url}
                      alt={activeData.title}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 cursor-pointer"
                      onClick={handleImageClick}
                    />
                    
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300 flex items-center justify-center pointer-events-none">
                      <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 shadow-lg">
                        <i className="ri-zoom-in-line text-3xl text-gray-900"></i>
                      </div>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6 pointer-events-none">
                      <p className="text-white font-semibold text-lg">
                        {activeData.title}
                      </p>
                      <p className="text-gray-300 text-sm">
                        Image {currentIndex + 1} of {activeData.images.length}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Next Image (Blurred) */}
                {activeData.images.length > 1 && (
                  <div className="hidden md:block w-1/4 px-2">
                    <div className="relative h-64 rounded-xl overflow-hidden opacity-40 blur-sm">
                      <img
                        src={activeData.images[(currentIndex + 1) % activeData.images.length].image_url}
                        alt="Next"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Navigation Arrows */}
            {activeData.images.length > 1 && (
              <>
                <button
                  onClick={handlePrevious}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-teal-600 hover:text-white transition-all duration-300 group z-10"
                  aria-label="Previous image"
                >
                  <i className="ri-arrow-left-s-line text-2xl"></i>
                </button>
                <button
                  onClick={handleNext}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-teal-600 hover:text-white transition-all duration-300 group z-10"
                  aria-label="Next image"
                >
                  <i className="ri-arrow-right-s-line text-2xl"></i>
                </button>
              </>
            )}
          </div>
        )}

        {/* Stats Section */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
          {categories.map((category) => (
            <div
              key={category.id}
              className="bg-white rounded-xl p-6 text-center border border-gray-200 hover:shadow-lg transition-all duration-300 cursor-pointer"
              onClick={() => setActiveCategory(category.id)}
            >
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className={`${category.icon} text-2xl text-teal-600`}></i>
              </div>
              <p className="text-3xl font-bold text-gray-900 mb-1">{category.images.length}</p>
              <p className="text-sm text-gray-600">{category.title}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox Modal */}
      {showLightbox && activeData.images.length > 0 && (
        <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4">
          {/* Close Button */}
          <button
            onClick={() => setShowLightbox(false)}
            className="absolute top-6 right-6 w-12 h-12 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition-all duration-300 z-20"
            aria-label="Close lightbox"
          >
            <i className="ri-close-line text-3xl"></i>
          </button>

          {/* Image Counter */}
          <div className="absolute top-6 left-6 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-white font-semibold z-20">
            {currentIndex + 1} / {activeData.images.length}
          </div>

          {/* Main Image */}
          <div className="relative w-full h-full flex items-center justify-center">
            <img
              src={activeData.images[currentIndex].image_url}
              alt={activeData.title}
              className="max-w-full max-h-full object-contain"
            />
          </div>

          {/* Navigation Arrows */}
          {activeData.images.length > 1 && (
            <>
              <button
                onClick={handleLightboxPrevious}
                className="absolute left-6 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition-all duration-300 z-20"
                aria-label="Previous image"
              >
                <i className="ri-arrow-left-s-line text-3xl"></i>
              </button>
              <button
                onClick={handleLightboxNext}
                className="absolute right-6 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition-all duration-300 z-20"
                aria-label="Next image"
              >
                <i className="ri-arrow-right-s-line text-3xl"></i>
              </button>
            </>
          )}

          {/* Image Info */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full text-white text-center z-20">
            <p className="font-semibold">{activeData.title}</p>
          </div>
        </div>
      )}
    </section>
  );
};
